/**
 * „Wyślij prośbę w czacie" — the recording request, sent the way mobile sends it (R18): the
 * client's email finds the direct conversation, the ask goes in as its own card, and the angle and
 * note follow as an ordinary message after it. The web never records and never uploads. What is
 * sent on a retry is `model/recordingRequest`'s decision.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { clientProfileQueryOptions } from '~/entities/client';
import {
  postChatConversationsConversationIdMessages,
  postChatConversationsConversationIdMessagesExerciseClipRequest,
  postChatConversationsDirectByEmail,
} from '~/shared/api/generated/endpoints/chat/chat';
import { useContentLanguage } from '~/shared/i18n/contentLanguage';

import {
  beginRequest,
  NO_REQUEST,
  requestFailed,
  requestHasNoConversation,
  stepSent,
  type RecordingRequestState,
  type RecordingRequestStatus,
} from '../model/recordingRequest';

export interface RecordingRequest {
  readonly status: RecordingRequestStatus;
  readonly send: (note: string) => void;
}

export function useRecordingRequest(clientId: number, exerciseId: number | null): RecordingRequest {
  const queryClient = useQueryClient();
  const lang = useContentLanguage();
  const [requests, setRequests] = useState<ReadonlyMap<number, RecordingRequestState>>(new Map());
  const state = exerciseId === null ? NO_REQUEST : (requests.get(exerciseId) ?? NO_REQUEST);

  return {
    status: state.status,
    send: (note) => {
      if (exerciseId === null) return;
      const started = beginRequest(state, () => crypto.randomUUID());
      if (started === null) return;
      const id = exerciseId;
      let current = started.state;
      const settle = (next: RecordingRequestState) => {
        current = next;
        setRequests((all) => new Map(all).set(id, next));
      };
      settle(current);
      void (async () => {
        try {
          const profile = await queryClient.fetchQuery(clientProfileQueryOptions(clientId));
          const email = profile.email?.trim() ?? '';
          if (email === '') {
            settle(requestHasNoConversation(current));
            return;
          }
          const conversation = await postChatConversationsDirectByEmail({ email });
          if (conversation.id === undefined) throw new Error('The conversation came back without an id.');
          for (const step of started.steps) {
            if (step === 'card') {
              await postChatConversationsConversationIdMessagesExerciseClipRequest(
                conversation.id,
                { exerciseId: id, clientId: started.ids.card },
                { lang },
              );
            } else {
              await postChatConversationsConversationIdMessages(conversation.id, {
                clientId: started.ids.note,
                content: note,
              });
            }
            settle(stepSent(current, step));
          }
        } catch {
          settle(requestFailed(current));
        }
      })();
    },
  };
}
